import './SortableTreeItem.css'

import React, { CSSProperties, forwardRef, HTMLAttributes } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDroppable } from '@dnd-kit/core'
import { AnimateLayoutChanges, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import clsx from 'clsx'

import { BuilderMenu } from '@makyoapp/makyo-common/dist/builder'
import {
  ArrowShortRight16,
  Check16,
  ChevronSmallDown,
  ChevronSmallRight,
  Close8,
  Copy16,
  Download16,
  Edit16,
  Reorder16,
  Trash16,
} from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant, MoreDropdown, Tooltip } from '@makyoapp/makyo-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { FlattenedItem } from '@/shared/builder/types'
import { getDataSourceKeyForComponent } from '@/shared/builder/utils/components'
import { ComponentIconMap } from '@/shared/icons'
import { getTextFromSlateValue } from '@/components/ActionV2/utils'

import { useSortableTreeContext } from '../contexts/SortableTreeContext'
import useTreeItem from '../hooks/useTreeItem'
import { filterParentDeep } from '../utils'
import FormErrorBadge from './FormErrorBadge'

const TRUNCATE_MAX_LENGTH = 20
const INDENTATION_OFFSET = 5

function calculateOffset(depth: number, indentationWidth: number) {
  function getDepthMultiplier(depth: number) {
    let base = 12
    let multiplier = depth === 0 ? 0 : depth * 0.5
    return base * multiplier
  }
  const offset = indentationWidth * depth + getDepthMultiplier(depth)

  return offset
}

function isValidPlaceholderDrop(
  itemId: string,
  activeItem?: FlattenedItem | undefined,
  itemsMap: Map<string, FlattenedItem> = new Map()
) {
  if (!activeItem) return true
  const itemCompId = itemsMap.get(itemId)?.compId
  if (!itemCompId) return true

  // prevent form component from being moved outside of a form container, could be nested
  if (activeItem.isForm || activeItem.isSubmit) {
    // check if it has form parent
    const isFormParent = filterParentDeep(
      itemsMap,
      itemId,
      (parent) => parent.compId === 'form' || parent.parentType === 'form'
    )

    return isFormParent
  }

  if (
    // prevent tab-item into tab-item
    (itemCompId === 'tab-item' && activeItem.compId === 'tab-item') ||
    // prevent tab-item into non tabs
    (activeItem.compId === 'tab-item' && itemCompId !== 'tabs')
  ) {
    return false
  }

  return true
}

//--------------------------------------------------------------------------------------//
//                                  Placeholder                                         //
//--------------------------------------------------------------------------------------//

const Placeholder = ({ handleClick, parentDepth, id, activeItem, indentationWidth }: any) => {
  const { flattenedItemsMap } = useSortableTreeContext()

  const { setNodeRef, isOver } = useDroppable({
    id: `placeholder-${id}`,
  })

  const offset = calculateOffset(parentDepth + 1, indentationWidth) + (indentationWidth - INDENTATION_OFFSET)

  const invalidDrop = !isValidPlaceholderDrop(id, activeItem, flattenedItemsMap)

  return (
    <div
      id={`placeholder-${id}`}
      ref={setNodeRef}
      className={clsx('cursor-pointer mb-1')}
      style={{
        paddingLeft: `${offset}px`,
      }}
      onClick={handleClick}
      role='presentation'
    >
      <div
        className={clsx(
          'w-[200px] h-[44px] px-4 py-2 flex items-center justify-start border border-dashed border-gray-300 rounded-lg',
          !isOver && 'hover:bg-gray-100',
          {
            ['bg-red-500/60 border-red-500']: isOver && invalidDrop,
            ['bg-primary-700/60 border-primary-500']: isOver && !invalidDrop,
          }
        )}
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          className='h-4 w-4 text-gray-400 mr-2'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
          strokeWidth={2}
        >
          <path strokeLinecap='round' strokeLinejoin='round' d='M12 4v16m8-8H4' />
        </svg>
        <span className='text-gray-500'>
          <Trans>Insert Component</Trans>
        </span>
      </div>
    </div>
  )
}

//--------------------------------------------------------------------------------------//
//                                  Tree Item                                           //
//--------------------------------------------------------------------------------------//

type TreeItemProps = HTMLAttributes<HTMLLIElement> & {
  id: string
  parentId?: string | null
  childCount?: number
  clone?: boolean
  collapsed?: boolean
  depth: number
  disableInteraction?: boolean
  disableSelection?: boolean
  ghost?: boolean
  handleProps?: any
  indicator?: boolean
  indentationWidth: number
  value: string
  title: string
  customTitle?: string
  icon: string
  nested: boolean
  nestableIndicator?: boolean
  wrapperRef?: (node: HTMLLIElement) => void
  active?: boolean
  formErrorType?: string
  removable?: boolean
  draggable?: boolean
  disableButton?: boolean
  isParentForm?: boolean
  showPlaceholder?: boolean
  isSubmit?: boolean
  overPlaceholder?: boolean
  compId: string
  groupId?: string
  activeItem?: FlattenedItem
  onPlaceholderClick?: () => void
  onCollapse?: () => void
}

const possibleDataValueKeys = [
  '-data-value',
  '-data-address',
  '-data-label',
  '-button-data-icon',
  '-data-src',
  '-data-source',
  '-list-data',
  '-data-icon',
]

const TreeItem = React.memo(
  forwardRef<HTMLDivElement, TreeItemProps>(
    (
      {
        id,
        parentId,
        childCount,
        clone,
        depth,
        disableSelection,
        disableInteraction,
        ghost,
        handleProps,
        indentationWidth = 24,
        indicator,
        collapsed,
        onCollapse,
        style,
        value,
        icon,
        title,
        customTitle,
        nested,
        nestableIndicator = false,
        wrapperRef,
        active = false,
        formErrorType,
        removable = true,
        draggable = true,
        disableButton = false,
        isParentForm,
        isSubmit,
        overPlaceholder,
        compId,
        groupId,
        ...props
      },
      ref
    ) => {
      // access the context
      const {
        clipboard,
        clipboardAll,
        handleCopy,
        handleRemove,
        handleRename,
        handleClone,
        formClipboard,
        handleCopyFormFields,
        handlePasteFormFields,
        handleRemoveFormFields,
        activeLayerElementSettings,
      } = useSortableTreeContext()

      const { tables } = useDbClientContext()
      const { menus, appClient, exportToAiJSON, copyComponentsToAiJSON } = useAppBuilderContext()
      const { activePageClient, activeLayer } = useActiveLayerContext()

      const getTableAndColumnInfo = (componentId: string) => {
        const globalsBodySettings = ((appClient?.builderSettings as any)?._Globals?._BodySettings || {}) as Record<
          string,
          any
        >
        const elSettings = globalsBodySettings[componentId] || activeLayerElementSettings[componentId]
        if (!elSettings) return null

        let dataSourceKey: string | null = null
        let tableId: any = null

        if (compId) {
          const correctDataSourceKey = getDataSourceKeyForComponent(compId)
          if (correctDataSourceKey && elSettings[correctDataSourceKey]) {
            dataSourceKey = correctDataSourceKey
            tableId = elSettings[correctDataSourceKey]
          }
        }

        if (!dataSourceKey || !tableId) {
          const dataSourceKeys = Object.keys(elSettings).filter((key) =>
            possibleDataValueKeys.some((suffix) => key.includes(suffix))
          )

          const preferredKey = dataSourceKeys.find((key) => key.includes('-data-source'))
          if (preferredKey) {
            dataSourceKey = preferredKey
            tableId = elSettings[preferredKey]
          } else if (dataSourceKeys.length > 0) {
            dataSourceKey = dataSourceKeys[0]
            tableId = elSettings[dataSourceKeys[0]]
          }
        }

        const dataValueKey = Object.keys(elSettings).find((key) => key.includes('-data-value'))
        let columnId = dataValueKey ? elSettings[dataValueKey] : null

        if (columnId && Array.isArray(columnId)) {
          const findTableAndColumn = (nodes: any[]): { tableId?: string; columnId?: string } => {
            for (const node of nodes) {
              if (node.tbl && node.col) {
                return { tableId: node?.tbl, columnId: node.col }
              }
              if (node.children && Array.isArray(node.children)) {
                const result = findTableAndColumn(node.children)
                if (result.tableId && result.columnId) {
                  return result
                }
              }
            }
            return {}
          }

          const extracted = findTableAndColumn(columnId)
          if (extracted.tableId) tableId = extracted.tableId
          if (extracted.columnId) columnId = extracted.columnId
        }

        if (!tableId) return null

        let actualTableId: string = tableId
        if (typeof tableId === 'object' && tableId !== null && 'table' in tableId) {
          actualTableId = tableId.table
        } else if (columnId && typeof columnId === 'object' && columnId !== null && 'tbl' in columnId) {
          actualTableId = columnId.tbl
        } else if (typeof tableId === 'string') {
          actualTableId = tableId
        }

        const table = tables.find((t) => t.id === actualTableId)
        const tableName = table?.name ? getTextFromSlateValue(table.name, tables) || 'Unknown Table' : 'Unknown Table'

        let columnName = null
        if (columnId && table) {
          let actualColumnId = columnId

          // Handle object with tbl and col properties
          if (typeof columnId === 'object' && columnId.tbl && columnId.col) {
            actualColumnId = columnId.col
          }
          // Handle array case
          else if (Array.isArray(columnId)) {
            actualColumnId = columnId[0]
          }
          // Handle object with value property
          else if (typeof columnId === 'object' && columnId.value) {
            actualColumnId = columnId.value
          }

          const column = table.columns.find((c) => c.id === actualColumnId)
          columnName = column?.name ? getTextFromSlateValue(column.name, tables) || 'Unknown Column' : 'Unknown Column'
        }

        let menuName = null
        if (elSettings['vertical-menu-data-source'] && menus) {
          const menu = menus.find((m: BuilderMenu) => m.id === elSettings['vertical-menu-data-source'])
          if (menu) {
            const rawMenuTitle = menu.title
            if (Array.isArray(rawMenuTitle)) {
              menuName = getTextFromSlateValue(rawMenuTitle, tables) || 'Unknown Menu'
            } else if (typeof rawMenuTitle === 'string') {
              const trimmedTitle = rawMenuTitle.trim()
              const looksLikeJson =
                (trimmedTitle.startsWith('[') && trimmedTitle.endsWith(']')) ||
                (trimmedTitle.startsWith('{') && trimmedTitle.endsWith('}'))

              if (looksLikeJson) {
                try {
                  const parsedTitle = JSON.parse(trimmedTitle)
                  if (Array.isArray(parsedTitle)) {
                    menuName = getTextFromSlateValue(parsedTitle, tables) || 'Unknown Menu'
                  } else if (parsedTitle && typeof parsedTitle === 'object') {
                    menuName = getTextFromSlateValue([parsedTitle], tables) || 'Unknown Menu'
                  } else {
                    menuName = rawMenuTitle
                  }
                } catch {
                  menuName = rawMenuTitle
                }
              } else {
                menuName = rawMenuTitle
              }
            } else if (rawMenuTitle && typeof rawMenuTitle === 'object' && 'text' in rawMenuTitle) {
              menuName = getTextFromSlateValue([rawMenuTitle as any], tables) || 'Unknown Menu'
            } else {
              menuName = 'Unknown Menu'
            }
          }
        }

        return { tableName, columnName, tableId, columnId, menuName }
      }

      const getComponentDataValue = (componentId: string) => {
        const globalsBodySettings = ((appClient?.builderSettings as any)?._Globals?._BodySettings || {}) as Record<
          string,
          any
        >
        const elSettings = globalsBodySettings[componentId] || activeLayerElementSettings[componentId]
        if (!elSettings) return null

        const dataValueKey = Object.keys(elSettings).find((key) =>
          possibleDataValueKeys.some((suffix) => key.includes(suffix))
        )
        return dataValueKey ? elSettings[dataValueKey] : null
      }

      const getComponentDataValueText = (dataValue: any): string => {
        if (!dataValue) return ''

        if (typeof dataValue === 'object') {
          if (dataValue?.tbl && dataValue.col) {
            const table = tables.find((t) => t.id === dataValue.tbl)
            if (table) {
              const column = table.columns.find((c) => c.id === dataValue.col)
              return column?.name || 'Unknown Column'
            }
          }
          if (dataValue.table && dataValue.columns) {
            return dataValue.columns.map((c: any) => c.name).join(', ')
          }
          return getTextFromSlateValue(dataValue, tables)
        }
        if (Array.isArray(dataValue)) {
          const textValue = getTextFromSlateValue(dataValue, tables)
          return textValue || ''
        }
        if (typeof dataValue === 'string') {
          return dataValue
        }

        return ''
      }

      const getComponentSuffix = (componentId: string, compId: string) => {
        const info = getTableAndColumnInfo(componentId)
        const { tableName, columnName, menuName } = info || {}

        switch (compId) {
          case 'form':
          case 'list':
          case 'card':
          case 'gallery':
          case 'accordion':
          case 'multi-location':
          case 'shopping-cart':
          case 'data-table':
          case 'data-list':
          case 'calendar':
          case 'pricing':
          case 'newsletter':
          case 'comment':
          case 'timeline':
          case 'map':
            return tableName || getComponentDataValueText(getComponentDataValue(componentId))

          case 'vertical-menu':
            return menuName || 'Screens'

          case 'text':
          case 'rich-text':
          case 'image':
          case 'audio':
          case 'pie':
          case 'bar':
          case 'rating':
          case 'icon':
            return columnName || getComponentDataValueText(getComponentDataValue(componentId))

          case 'button':
            return columnName || getComponentDataValueText(getComponentDataValue(componentId)) || 'Submit'

          case 'quantity':
            return `${tableName} -> ${columnName}`

          case 'text-field':
          case 'checkbox':
          case 'switch':
          case 'selection':
          case 'slider':
          case 'date-time-picker':
          case 'file-upload':
          case 'image-upload':
          case 'hidden-field':
          case 'linear-progress':
          case 'circular-progress':
            return columnName

          case 'alert':
            return columnName || 'Title'

          default:
            return ''
        }
      }

      const { t } = useTranslation()
      const { rename, inputRef, tempName, handleRenameInput, handleRenameKeydown, setRename, handleSaveRename } =
        useTreeItem(title, customTitle, (next) => handleRename(id, next))

      const offset = calculateOffset(depth, indentationWidth)

      const renderTitle = () => {
        const suffix = getComponentSuffix(id, compId)
        const displaySuffix = suffix
          ? `(${suffix?.length > TRUNCATE_MAX_LENGTH ? suffix?.slice(0, TRUNCATE_MAX_LENGTH) + '...' : suffix})`
          : ''

        const truncateText = (text: string, maxLength: number = TRUNCATE_MAX_LENGTH) => {
          if (text.length <= maxLength) return `${text} ${displaySuffix}`
          return text.substring(0, maxLength) + '...'
        }

        return (
          <div className='select-none pointer-events-none'>
            {customTitle ? (
              <div className='txt flex-c'>
                <span className='txt' title={customTitle}>
                  {truncateText(customTitle)}
                </span>
                <span className='text-m-sm text-light' title={title}>
                  ({truncateText(title)})
                </span>
              </div>
            ) : (
              <div className='txt' title={title}>
                {truncateText(title)}
              </div>
            )}
          </div>
        )
      }

      const [confirmOpen, setConfirmOpen] = React.useState(false)
      const [skipGlobalDeleteConfirm, setSkipGlobalDeleteConfirm] = React.useState<boolean>(() => {
        try {
          return localStorage.getItem('makyo.hideGlobalDeleteConfirm') === '1'
        } catch {
          return false
        }
      })
      const handleToggleSkipConfirm = (checked: boolean) => {
        setSkipGlobalDeleteConfirm(checked)
        try {
          if (checked) {
            localStorage.setItem('makyo.hideGlobalDeleteConfirm', '1')
          } else {
            localStorage.removeItem('makyo.hideGlobalDeleteConfirm')
          }
        } catch {
          // ignore storage failures
        }
      }

      // check if this id belongs to globals subtree
      const isInGlobals = React.useMemo(() => {
        const globals = ((appClient?.builderSettings as any)?._Globals || { _Body: [] }) as any
        const containsInBodies = (bodies: any[], targetId: string): boolean => {
          for (const b of bodies) {
            if (b._id === targetId) return true
            if (Array.isArray(b._Body) && b._Body.length && containsInBodies(b._Body, targetId)) return true
          }
          return false
        }
        return containsInBodies(globals._Body || [], id)
      }, [appClient?.builderSettings, id])

      return (
        <li
          ref={wrapperRef}
          style={
            {
              '--tree-spacing': `${offset}px`,
            } as React.CSSProperties
          }
          className={clsx(
            'tree-wrapper text-m-m box-border list-none cursor-pointer pl-(--tree-spacing)',
            clone && 'clone',
            ghost && 'ghost',
            indicator && 'indicator',
            disableSelection && 'no-select',
            disableInteraction && 'no-interact',
            active &&
              !ghost &&
              'rounded-md ring-1 ring-inset ring-yellow-400 dark:ring-yellow-600 bg-zinc-100 dark:bg-slate-700'
          )}
          {...props}
        >
          <div
            ref={ref}
            data-id={value}
            data-depth={depth}
            data-title={title}
            className={clsx(
              'tree-item group relative flex items-center gap-1 py-1',
              !overPlaceholder && {
                'ml-2 relative p-0 h-[44px] w-[250px] rounded-md border border-primary-500': ghost && indicator,
                'border-primary-500 bg-primary-500/80': ghost && indicator && nestableIndicator,
                'border-red-500 bg-red-500/80': ghost && indicator && !nestableIndicator,
              }
            )}
            style={style}
          >
            <React.Fragment>
              {groupId === 'globals' && parentId == null ? (
                <div className='aspect-square h-[36px] w-[36px] flex items-center justify-center'>
                  <MaterialIcon icon='push_pin' className='text-orange-500' size={18} />
                </div>
              ) : (
                <button
                  className='cursor-grab touch-none hover:text-primary-500 aspect-square h-[36px] w-[36px] flex items-center justify-center'
                  {...handleProps}
                >
                  <Reorder16 />
                </button>
              )}

              {!rename ? (
                <div className='flex-c w-full txt' title={customTitle ? `${customTitle} (${title})` : title}>
                  <MaterialIcon icon={ComponentIconMap[compId]} className='shrink-0' size={18} />
                  {renderTitle()}
                  {onCollapse && (
                    <Button
                      variant={ButtonVariant.Ghost}
                      noPadding
                      className='shrink-0'
                      squared
                      onClick={(e) => {
                        e.stopPropagation()
                        onCollapse()
                      }}
                      icon={!collapsed ? <ChevronSmallDown /> : <ChevronSmallRight />}
                    />
                  )}
                  {clipboard && clipboard.elId === id && (
                    <MaterialIcon icon={'content_copy'} className='text-light shrink-0' size={14} />
                  )}
                  {formClipboard && formClipboard.elID === id && (
                    <MaterialIcon icon={'file_copy'} className='text-light shrink-0' size={14} />
                  )}
                </div>
              ) : (
                // If the component is not in rename mode
                <div className='flex-c w-full'>
                  <input
                    ref={inputRef}
                    className='w-full bg-zinc-200 dark:bg-slate-600 p-2'
                    type='text'
                    value={tempName}
                    onChange={(e) => handleRenameInput(e.target.value)}
                    onKeyDown={handleRenameKeydown}
                  />
                  <Button
                    variant={ButtonVariant.Normal}
                    icon={<Check16 />}
                    noTip
                    className='shrink-0'
                    title={t('Save')}
                    noPadding
                    squared
                    onClick={handleSaveRename}
                  />
                  <Button
                    variant={ButtonVariant.Ghost}
                    icon={<Close8 />}
                    noTip
                    className='shrink-0'
                    title={t('Cancel')}
                    noPadding
                    squared
                    onClick={() => {
                      setRename(false)
                    }}
                  />
                </div>
              )}

              <div className='flex'>
                {formErrorType && <FormErrorBadge errorType={formErrorType} />}

                {!rename && (
                  <Button
                    variant={ButtonVariant.Ghost}
                    onClick={(e) => {
                      handleRenameInput(customTitle || title)
                      e.stopPropagation()
                      setRename(true)
                      setTimeout(() => {
                        inputRef.current?.focus()
                      })
                    }}
                    noTip
                    title={t('Rename')}
                    squared
                    noPadding
                    icon={<Edit16 />}
                  />
                )}

                {!clone && !rename && groupId !== 'globals' && (
                  <Button
                    variant={ButtonVariant.Ghost}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleClone(id)
                    }}
                    noTip
                    title={t('Clone')}
                    squared
                    noPadding
                    icon={<Copy16 />}
                    disabled={disableButton || isSubmit}
                  />
                )}

                {/* {!clone && !rename && (
                  <Button
                    variant={ButtonVariant.Ghost}
                    onClick={() => {
                      exportToAiJSON('components', { id, layerId: activeLayer.id, layerType: activeLayer.type })
                    }}
                    noTip
                    title={t('Export JSON')}
                    squared
                    noPadding
                  />
                )} */}

                {/* Globals visibility toggle */}
                {!clone && !rename && groupId === 'globals' && activeLayer.type === 'page' && (
                  <Button
                    variant={ButtonVariant.Ghost}
                    onClick={(e) => {
                      e.stopPropagation()
                      const isHeader = compId === 'header'
                      const current = (activePageClient?.settings as any)?._GlobalSettings || {}
                      const nextVisible = (() => {
                        const isHidden = isHeader ? current._HideHeader === true : current._HideFooter === true
                        return !isHidden
                      })()
                      appClient.UpdatePageGlobalsVisibility(activePageClient!.settings._id, {
                        [isHeader ? 'header' : 'footer']: !nextVisible,
                      } as any)
                    }}
                    noTip
                    title={t('Toggle Visibility')}
                    squared
                    noPadding
                    icon={
                      ((activePageClient?.settings as any)?._GlobalSettings || {})[
                        compId === 'header' ? '_HideHeader' : '_HideFooter'
                      ] === true ? (
                        <MaterialIcon icon='visibility_off' size={16} />
                      ) : (
                        <MaterialIcon icon='visibility' size={16} />
                      )
                    }
                  />
                )}

                {!clone &&
                  !rename &&
                  removable !== false &&
                  (() => {
                    if (isInGlobals) {
                      return (
                        <div className='relative'>
                          <Button
                            variant={ButtonVariant.Ghost}
                            onClick={(e) => {
                              e.stopPropagation()
                              if (skipGlobalDeleteConfirm) {
                                handleRemove(id)
                              } else {
                                setConfirmOpen((v) => !v)
                              }
                            }}
                            noTip
                            title={t('Remove')}
                            squared
                            noPadding
                            icon={<Trash16 />}
                            disabled={disableButton || isSubmit}
                          />
                          {confirmOpen && (
                            <div className='absolute z-20 right-0 top-10 min-w-[260px] p-3 rounded-md border bg-white dark:bg-slate-900 shadow-lg'>
                              <div className='text-m-s mb-3'>
                                <Trans>This is a global component. Deleting will affect all pages.</Trans>
                              </div>
                              <label className='flex items-center gap-2 text-m-s mb-3 select-none'>
                                <input
                                  type='checkbox'
                                  checked={skipGlobalDeleteConfirm}
                                  onChange={(ev) => {
                                    ev.stopPropagation()
                                    handleToggleSkipConfirm(ev.target.checked)
                                  }}
                                />
                                <span>
                                  <Trans>Don&rsquo;t show again</Trans>
                                </span>
                              </label>
                              <div className='flex gap-2 justify-end'>
                                <Button
                                  variant={ButtonVariant.Ghost}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setConfirmOpen(false)
                                  }}
                                >
                                  <Trans>Cancel</Trans>
                                </Button>
                                <Button
                                  variant={ButtonVariant.Normal}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setConfirmOpen(false)
                                    handleRemove(id)
                                  }}
                                >
                                  <Trans>Continue</Trans>
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    }
                    return (
                      <Button
                        variant={ButtonVariant.Ghost}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemove(id)
                        }}
                        noTip
                        title={t('Remove')}
                        squared
                        noPadding
                        icon={<Trash16 />}
                        disabled={disableButton || isSubmit}
                      />
                    )
                  })()}

                {clone && childCount && childCount > 1 ? (
                  <div className='inline-flex items-center justify-center pr-1 mr-1'>
                    <span className='text-white bg-primary-500 text-m-sm rounded-full px-2 py-1'>{childCount}</span>
                  </div>
                ) : null}

                {!rename && !clone && (
                  <MoreDropdown
                    menuBtnClassName={clsx(
                      'w-9 h-9 flex-cc hover:bg-zinc-100 dark:hover:bg-slate-900 rounded-sm transition-colors',
                      (isSubmit || groupId === 'globals') && 'cursor-not-allowed opacity-20 pointer-events-none'
                    )}
                    menuClassName='z-10'
                    placement='left-start'
                  >
                    {/* Show remove fields for form component */}
                    {isParentForm && (
                      <>
                        <MoreDropdown.MenuItem onClick={() => handleCopyFormFields(id)} className='justify-between'>
                          <div className='flex-c'>
                            <MaterialIcon icon={'file_copy'} className='text-yellow-500 shrink-0' size={16} />
                            <span>
                              <Trans>Copy Fields (All)</Trans>
                            </span>
                          </div>

                          <Tooltip
                            content={
                              <div className='max-w-[186px] text-m-s flex-c'>
                                <Trans>
                                  Copy all elements of this form, including fields, submit button, and other components.
                                </Trans>
                              </div>
                            }
                          >
                            <MaterialIcon icon='info' size={14} />
                          </Tooltip>
                        </MoreDropdown.MenuItem>

                        {formClipboard && formClipboard.elID !== id && (
                          <MoreDropdown.MenuItem onClick={() => handlePasteFormFields(id)} className='justify-between'>
                            <div className='flex-c'>
                              <MaterialIcon icon={'content_paste_go'} className='text-primary-500 shrink-0' size={16} />
                              <span>
                                <Trans>Paste Fields (All)</Trans>
                              </span>
                            </div>

                            <Tooltip
                              content={
                                <div className='max-w-[200px] py-1 text-m-s space-y-1.5'>
                                  <div>Paste all elements from:</div>

                                  <div className='text-m-s flex-c py-1.5 px-3 bg-slate-300 rounded-md'>
                                    <div className='flex-c tracking-tight'>{formClipboard.pageName}</div>
                                    <ArrowShortRight16 />
                                    <div className='flex-c tracking-tight truncate'>
                                      <MaterialIcon
                                        icon={ComponentIconMap[formClipboard.elComponentID]}
                                        className='shrink-0'
                                        size={14}
                                      />
                                      {formClipboard.elName}
                                    </div>
                                  </div>

                                  <div>
                                    <Trans>
                                      This will replace all elements in this form, including submit button and other
                                      components.
                                    </Trans>
                                  </div>
                                </div>
                              }
                            >
                              <MaterialIcon icon='info' size={14} />
                            </Tooltip>
                          </MoreDropdown.MenuItem>
                        )}

                        <MoreDropdown.MenuItem onClick={() => handleRemoveFormFields(id)} className='justify-between'>
                          <div className='flex-c'>
                            <MaterialIcon icon={'delete'} className='text-red-500 shrink-0' size={16} />
                            <span>
                              <Trans>Remove Fields</Trans>
                            </span>
                          </div>
                          <Tooltip
                            content={
                              <div className='max-w-[186px] text-m-s'>
                                <Trans>This will remove all elements in this form, except submit button.</Trans>
                              </div>
                            }
                          >
                            <MaterialIcon icon='info' size={14} />
                          </Tooltip>
                        </MoreDropdown.MenuItem>
                      </>
                    )}

                    <MoreDropdown.MenuItem onClick={() => handleCopy(id)}>
                      <MaterialIcon icon={'content_copy'} className='text-sky-500 shrink-0' size={16} />
                      <span>
                        <Trans>Copy Element</Trans>
                      </span>
                    </MoreDropdown.MenuItem>

                    {/* Export to JSON */}
                    <MoreDropdown.MenuItem
                      onClick={() =>
                        exportToAiJSON('components', { id, layerId: activeLayer.id, layerType: activeLayer.type })
                      }
                    >
                      <MaterialIcon icon='download' className='text-primary-500 shrink-0' size={16} />
                      <span>
                        <Trans>Export JSON</Trans>
                      </span>
                    </MoreDropdown.MenuItem>

                    {/* Copy to JSON */}
                    <MoreDropdown.MenuItem onClick={() => copyComponentsToAiJSON(id, activeLayer.id, activeLayer.type)}>
                      <MaterialIcon icon='content_copy' className='text-primary-500 shrink-0' size={16} />
                      <span>
                        <Trans>Copy JSON</Trans>
                      </span>
                    </MoreDropdown.MenuItem>
                  </MoreDropdown>
                )}
              </div>
            </React.Fragment>
          </div>
        </li>
      )
    }
  )
)

//--------------------------------------------------------------------------------------//
//                                  Sortable Tree Item                                  //
//--------------------------------------------------------------------------------------//

const animateLayoutChanges: AnimateLayoutChanges = ({ isSorting, wasDragging }) =>
  isSorting || wasDragging ? false : true

function SortableItem({ id, depth, showPlaceholder, onPlaceholderClick, activeItem, ...props }: TreeItemProps) {
  // useSortable is a hook from DndKit that provides the draggable functionality
  const {
    attributes,
    isDragging,
    isSorting,
    listeners,
    setDraggableNodeRef,
    setDroppableNodeRef,
    transform,
    transition,
    over,
  } = useSortable({
    id,
    animateLayoutChanges,
  })

  const overPlaceholder = over?.id.toString().startsWith('placeholder-')

  // Style is used to transform and transition the draggable items
  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  }

  return (
    // TreeItem is the actual component that is being dragged and sorted
    <>
      <TreeItem
        id={id}
        ref={setDraggableNodeRef}
        wrapperRef={setDroppableNodeRef}
        style={style}
        depth={depth}
        ghost={isDragging}
        disableSelection={/iPad|iPhone|iPod/.test(navigator.platform)}
        disableInteraction={isSorting}
        handleProps={{
          ...attributes,
          ...listeners,
        }}
        disableButton={false}
        overPlaceholder={overPlaceholder}
        {...props}
      />
      {showPlaceholder && (
        <Placeholder
          parentDepth={depth}
          id={id}
          activeItem={activeItem}
          handleClick={onPlaceholderClick}
          indentationWidth={props.indentationWidth}
        />
      )}
    </>
  )
}

export const SortableTreeItem = React.memo(SortableItem)
